/**
 * `useDuplicateApprovalChain` mutation hook.
 *
 * Wraps the raw `duplicateApprovalChain` service fn (per AD-23). The backend creates
 * a full copy (steps + assignees) within the same project; we invalidate the
 * project's approval chain queries so the new row appears.
 *
 * Side effects: NONE. Toast UX belongs to the caller (the list view).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/approval-chain';

import { duplicateApprovalChain } from './approval-chains.api';

export { duplicateApprovalChain };

interface UseDuplicateApprovalChainOptions {
  projectId: string;
}

export function useDuplicateApprovalChain(options: UseDuplicateApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation({
    mutationFn: (approvalChainId: string) => duplicateApprovalChain(projectId, approvalChainId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });
    },
  });
}
